import {
  Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';

import { AuthState } from '../auth/store/auth.state';
import { CapabilitiesState } from './capabilities.state';
import { CapabilitiesStateModel } from './capabilities.state';

/** Las tres presentaciones posibles de un elemento sujeto a capacidad. */
export type PresentacionSinCapacidad = 'disabled' | 'hidden';

/**
 * Presenta un elemento según el conjunto efectivo de capacidades.
 *
 * Existe una sola directiva, y no una condición repetida en cada plantilla, porque la regla es
 * delicada y reimplementarla cinco veces garantiza que alguna salga distinta. Aquí está escrita una
 * vez y las cinco superficies la comparten.
 *
 * Decide entre tres presentaciones:
 *
 * * **Disponible**: la capacidad está concedida.
 * * **Deshabilitada con motivo**: no está concedida, pero tiene sentido que el usuario sepa que
 *   existe. Se deshabilita de verdad —no solo con estilo— y se le pone el motivo legible.
 * * **Oculta**: no está concedida y enseñarla no aporta nada.
 *
 * Lo que **nunca** hace es dejar pulsable una acción que va a fallar. Una interfaz que ofrece un
 * botón y contesta con un error al pulsarlo es peor que una que no lo ofrece: el usuario no sabe si
 * se ha equivocado él, si hay una avería, o si no tiene permiso.
 *
 * **Mientras el conjunto no se conoce, nada se presenta como disponible.** Es el estado que suele
 * olvidarse: durante la carga no se sabe qué hay concedido, y dar por bueno lo que había antes
 * ofrece acciones del proyecto anterior.
 *
 * Nada de esto autoriza. El control vive en el backend, y quien llame al endpoint a mano recibe su
 * 403 con o sin botón a la vista.
 */
@Directive({
  selector: '[appCapability]',
  exportAs: 'appCapability',
  standalone: false
})
export class CapabilityDirective implements OnInit, OnDestroy {
  /** Código de la capacidad exigida. */
  @Input('appCapability') capability = '';

  /** Qué hacer cuando no está concedida. Por defecto se deshabilita, que informa más que ocultar. */
  @Input() appCapabilityFallback: PresentacionSinCapacidad = 'disabled';

  /** Clave de traducción del motivo, para que la plantilla pueda mostrarlo si quiere. */
  motivo: string | null = null;

  private suscripcion?: Subscription;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly store: Store
  ) {}

  ngOnInit(): void {
    this.suscripcion = this.store
      .select((estado: { capabilities: CapabilitiesStateModel }) => estado.capabilities)
      .subscribe(() => this.aplicar());
  }

  ngOnDestroy(): void {
    this.suscripcion?.unsubscribe();
  }

  private aplicar(): void {
    // Un elemento sin capacidad declarada no esta sujeto a ninguna: son los agentes que el catalogo
    // no contempla, cuyo acceso sigue rigiendo el rol de proyecto. Deshabilitarlos aqui seria mas
    // restrictivo que el backend, que es tan incorrecto como serlo menos.
    if (!this.capability) {
      this.mostrarDisponible();
      return;
    }
    const cargando = this.store.selectSnapshot(CapabilitiesState.loading);
    const conjunto = this.store.selectSnapshot(CapabilitiesState.effective);
    const concedida = this.store.selectSnapshot(CapabilitiesState.has(this.capability));

    // Mientras no se sabe, no se ofrece. Tratar la carga como "concedido" es justo lo que deja
    // pulsar acciones del proyecto anterior durante el cambio.
    const disponible = !cargando && !!conjunto && concedida;

    if (disponible) {
      this.mostrarDisponible();
      return;
    }
    // Sin proyecto elegido el conjunto no llega nunca: las capacidades se piden al seleccionarlo.
    // Decir "comprobando" en ese caso deja al usuario esperando algo que no va a ocurrir; lo que
    // necesita saber es que le falta elegir proyecto.
    const sinProyecto = !this.store.selectSnapshot(AuthState.getSelectedProject)?.id;
    this.motivo = sinProyecto ? 'CAPABILITIES.REASON.NO_PROJECT' : this.motivoDe(conjunto, cargando);
    if (this.appCapabilityFallback === 'hidden') {
      this.ocultar();
    } else {
      this.deshabilitar();
    }
  }

  private mostrarDisponible(): void {
    this.motivo = null;
    this.renderer.removeStyle(this.el.nativeElement, 'display');
    this.renderer.removeAttribute(this.el.nativeElement, 'disabled');
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-disabled');
    this.renderer.removeAttribute(this.el.nativeElement, 'title');
    this.renderer.removeClass(this.el.nativeElement, 'sin-capacidad');
  }

  private deshabilitar(): void {
    this.renderer.removeStyle(this.el.nativeElement, 'display');
    // `disabled` de verdad y no solo una clase: con clase, el elemento sigue siendo pulsable con
    // teclado y el usuario acaba lanzando una accion que el backend va a rechazar.
    this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
    this.renderer.setAttribute(this.el.nativeElement, 'aria-disabled', 'true');
    this.renderer.addClass(this.el.nativeElement, 'sin-capacidad');
    if (this.motivo) {
      this.renderer.setAttribute(this.el.nativeElement, 'data-motivo', this.motivo);
    }
  }

  private ocultar(): void {
    this.renderer.setStyle(this.el.nativeElement, 'display', 'none');
    this.renderer.setAttribute(this.el.nativeElement, 'aria-hidden', 'true');
  }

  /**
   * Clave de traducción del motivo. Nunca el texto: las etiquetas se resuelven por traducción.
   *
   * Se distingue el eje que falló porque al usuario le sirve saberlo —si es el rol, lo arregla su
   * administrador; si es el perfil, se lo cambian—, pero no se expone la política completa ni traza
   * técnica: eso le diría a cualquiera cómo está montado el control.
   */
  private motivoDe(conjunto: ReturnType<typeof CapabilitiesState.effective>, cargando: boolean): string {
    if (cargando || !conjunto) {
      return 'CAPABILITIES.REASON.LOADING';
    }
    const noConcedida = conjunto.notGranted.find(n => n.capability === this.capability);
    switch (noConcedida?.failedAxis) {
      case 'ROLE':
        return 'CAPABILITIES.REASON.ROLE';
      case 'PROFILE':
        return 'CAPABILITIES.REASON.PROFILE';
      case 'BOTH':
        return 'CAPABILITIES.REASON.BOTH';
      default:
        return 'CAPABILITIES.REASON.UNKNOWN';
    }
  }
}
